// src/views/admin/ADProfils.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";

function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const F = ({ label, placeholder = "", required = false, value, onChange, textarea = false }) => (
  <div className="fg">
    <label>{label} {required && <span style={{ color: "var(--rd)" }}>*</span>}</label>
    {textarea ? (
      <textarea rows={3} placeholder={placeholder} value={value ?? ""} onChange={onChange} />
    ) : (
      <input type="text" placeholder={placeholder} value={value ?? ""} onChange={onChange} />
    )}
  </div>
);

const EMPTY = { nom: "", description: "", actif: true };

export default function ADProfils({ slug }) {
  const [profils, setProfils] = useState([]);
  const [catalogue, setCatalogue] = useState([]); // permissions actives, groupées ensuite par module
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // create | edit | delete | permissions
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pickedPermIds, setPickedPermIds] = useState([]);

  const toast = useToastHook();
  const {
    getProfilsAdministrateurs, createProfilAdministrateur,
    updateProfilAdministrateur, deleteProfilAdministrateur,
    getPermissionsAdmin,
  } = useAdminService();

  useEffect(() => { if (slug) init(); }, [slug]);

  const init = async () => {
    setLoading(true);
    try {
      const [profilsData, permsData] = await Promise.all([
        getProfilsAdministrateurs(slug),
        getPermissionsAdmin(slug),
      ]);
      setProfils(profilsData);
      setCatalogue(permsData);
    } catch (error) {
      toast.error("Impossible de charger les profils administrateurs");
    } finally {
      setLoading(false);
    }
  };

  const reloadProfils = async () => {
    const data = await getProfilsAdministrateurs(slug);
    setProfils(data);
  };

  // Regroupement du catalogue de permissions par module
  const permsByModule = catalogue.reduce((acc, p) => {
    if (!acc[p.module]) acc[p.module] = [];
    acc[p.module].push(p);
    return acc;
  }, {});

  const filtered = profils.filter(p => p.nom?.toLowerCase().includes(search.toLowerCase()));

  // ─── Créer / modifier (infos générales) ────────────────────────────────
  const openCreate = () => { setForm(EMPTY); setModal("create"); };
  const openEdit = (p) => {
    setSelected(p);
    setForm({ nom: p.nom, description: p.description || "", actif: p.actif });
    setModal("edit");
  };
  const openDelete = (p) => { setSelected(p); setModal("delete"); };

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error("Le nom du profil est requis"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        await createProfilAdministrateur(slug, form);
        toast.success(`Profil "${form.nom}" créé avec succès`);
      } else {
        await updateProfilAdministrateur(slug, selected.id, form);
        toast.success(`Profil "${form.nom}" modifié avec succès`);
      }
      setModal(null);
      await reloadProfils();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data ? Object.values(data).flat().join("\n") : "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteProfilAdministrateur(slug, selected.id);
      toast.success(`Profil "${selected.nom}" supprimé avec succès`);
      setModal(null);
      await reloadProfils();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer ce profil");
    } finally {
      setSaving(false);
    }
  };

  // ─── Assignation des permissions ───────────────────────────────────────
  const openPermissions = (p) => {
    setSelected(p);
    setPickedPermIds((p.permissions_detail || []).map(perm => perm.id));
    setModal("permissions");
  };

  const togglePerm = (id) => {
    setPickedPermIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleModule = (module) => {
    const idsModule = permsByModule[module].map(p => p.id);
    const toutCoche = idsModule.every(id => pickedPermIds.includes(id));
    setPickedPermIds(prev => toutCoche
      ? prev.filter(id => !idsModule.includes(id))
      : [...new Set([...prev, ...idsModule])]
    );
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      const updated = await updateProfilAdministrateur(slug, selected.id, { permissions: pickedPermIds });
      setProfils(prev => prev.map(p => p.id === selected.id ? updated : p));
      toast.success("Permissions mises à jour avec succès");
      setModal(null);
    } catch (error) {
      toast.error("Impossible de mettre à jour les permissions");
    } finally {
      setSaving(false);
    }
  };

  const toggleActif = async (p) => {
    try {
      const updated = await updateProfilAdministrateur(slug, p.id, { actif: !p.actif });
      setProfils(prev => prev.map(x => x.id === p.id ? updated : x));
      toast.success(`Profil ${!p.actif ? "activé" : "désactivé"} avec succès`);
    } catch (error) {
      toast.error("Impossible de modifier le statut");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <button className="bprim" onClick={openCreate}>
          <i className="ti ti-shield-plus" /> Nouveau profil
        </button>
        <input
          type="text"
          placeholder="Rechercher un profil…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 200 }}
        />
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl" style={{ width: "100%", tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Profil</th>
              <th style={{ width: "30%" }}>Description</th>
              <th style={{ width: "14%" }}>Permissions</th>
              <th style={{ width: "12%" }}>Utilisateurs</th>
              <th style={{ width: "10%" }}>Statut</th>
              <th style={{ width: "12%" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 500 }}>{p.nom}</td>
                <td style={{ color: "var(--m)", fontSize: 13 }}>{p.description || "—"}</td>
                <td>{p.permissions_detail?.length || 0}</td>
                <td>{p.nb_utilisateurs}</td>
                <td>
                  <span className={`pill ${p.actif ? "ok" : "neu"}`}>{p.actif ? "Actif" : "Inactif"}</span>
                </td>
                <td>
                  <div className="act">
                    <button
                      title="Gérer les permissions"
                      onClick={() => openPermissions(p)}
                      style={{ background: "#6f42c1", color: "white" }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                    >
                      <i className="ti ti-shield-lock" />
                    </button>
                    <button
                      title="Modifier"
                      onClick={() => openEdit(p)}
                      style={{ background: "#ffc107", color: "black" }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                    >
                      <i className="ti ti-edit" />
                    </button>
                    <button
                      title={p.actif ? "Désactiver" : "Activer"}
                      onClick={() => toggleActif(p)}
                      style={{ background: p.actif ? "#fd7e14" : "#28a745", color: "white" }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                    >
                      <i className={`ti ${p.actif ? "ti-lock" : "ti-lock-open"}`} />
                    </button>
                    <button className="del" title="Supprimer" onClick={() => openDelete(p)}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "40px" }}>
                  Aucun profil administrateur trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Créer/Modifier */}
      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouveau profil administrateur" : "Modifier le profil"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <F
              label="Nom du profil"
              placeholder="ex: Gestionnaire scolarité"
              required
              value={form.nom}
              onChange={e => handleFormChange('nom', e.target.value)}
            />
            <F
              label="Description"
              placeholder="Rôle et périmètre de ce profil"
              textarea
              value={form.description}
              onChange={e => handleFormChange('description', e.target.value)}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <input
                type="checkbox"
                id="p-actif"
                checked={!!form.actif}
                onChange={e => handleFormChange('actif', e.target.checked)}
              />
              <label htmlFor="p-actif" style={{ fontSize: 13, color: "var(--m)" }}>Profil actif</label>
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : (modal === "create" ? "Créer" : "Enregistrer")}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Permissions, groupées par module */}
      {modal === "permissions" && selected && (
        <Modal title={`Permissions — ${selected.nom}`} onClose={() => setModal(null)} maxWidth={640}>
          <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
            {Object.entries(permsByModule).map(([module, perms]) => {
              const idsModule = perms.map(p => p.id);
              const toutCoche = idsModule.every(id => pickedPermIds.includes(id));
              return (
                <div key={module}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid var(--b)",
                  }}>
                    <span style={{ fontWeight: 600, fontSize: 13, textTransform: "uppercase", color: "var(--t)" }}>
                      {module}
                    </span>
                    <button className="bsec" style={{ padding: "3px 8px", fontSize: 12 }} onClick={() => toggleModule(module)}>
                      {toutCoche ? "Tout décocher" : "Tout cocher"}
                    </button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {perms.map(perm => (
                      <label key={perm.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={pickedPermIds.includes(perm.id)}
                          onChange={() => togglePerm(perm.id)}
                        />
                        {perm.libelle}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            {Object.keys(permsByModule).length === 0 && (
              <div style={{ color: "var(--h)", fontSize: 13 }}>Aucune permission disponible dans le catalogue.</div>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSavePermissions} disabled={saving}>
              {saving ? "En cours..." : "Enregistrer"}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Suppression */}
      {modal === "delete" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
            Êtes-vous sûr de vouloir supprimer le profil <br />
            <strong style={{ color: "var(--t)", fontSize: 16 }}>{selected.nom}</strong>
            <div style={{ marginTop: 8, fontSize: 13, color: "var(--rd)" }}>
              ⚠️ Cette action est irréversible.
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving ? "Suppression..." : (<><i className="ti ti-trash" /> Supprimer</>)}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        .act button {
          width: 30px; height: 30px; border-radius: 4px; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center; font-size: 14px; transition: all .2s;
        }
        .act button:hover { opacity: 0.8 !important; }
        .act button.del { background: var(--rd); color: white; }
        .act button.del:hover { background: var(--rd-dk); }
      `}</style>
    </div>
  );
}
